import { useEffect, useState } from "react";

import {
  Loader,
  Notification,
  TextTooltip,
} from "@transfr-inc/dashboard-components";
import { Button, ToggleButton } from "@transfr-inc/dashboard-components/forms";
import {
  PageHeader,
  PageLayout,
} from "@transfr-inc/dashboard-components/layouts";

import {
  AddIntegrationModal,
  RosterSyncConfirmModal,
} from "@components/modals";
import { useApiRequest } from "@lib/hooks";
import { Role } from "@models/role";
import { useCurrentUser, useOrganizationDetails, useRole } from "@queries";
import { useConnectionProviderLastRun } from "@queries/connection";
import { useProviders } from "@queries/integration";
import { useContainer } from "@src/contexts/container-context";
import { useQueryClient } from "@tanstack/react-query";

import { useUpdateOrganization } from "@hooks/queries";
import {
  useNotifications,
  useOAuthFlow,
  useParentOrgPagination,
  useSyncStatusPolling,
} from "@views/integrations/hooks";
import {
  buildStaticConnectionFromLastRun,
  canShowRosterSync,
  getStaticIntegrationContext,
} from "@views/integrations/utils";

import IntegrationSelect from "./components/integration-select";
import IntegrationTable from "./components/integration-table";
import { ZeroStateMessage } from "./zero-state-message";

import "./index.scss";

/**
 * Main integrations list page component.
 *
 * This component handles displaying and managing integrations for both single-org
 * and multi-org (parent) users. It supports two types of connections:
 *
 * 1. Static connections: Pre-configured integrations (ClassLink/Clever) that are
 *    automatically available based on the user's IDP. These cannot be added/removed.
 *
 * 2. Dynamic connections: User-configured integrations that can be added, removed,
 *    and manually synced.
 *
 * Flow:
 * - For parent org users: Loads child organizations and their connections with pagination
 * - For single org users: Loads connections directly or displays static connection
 * - Handles OAuth flow for adding new integrations
 * - Polls sync status after manual syncs
 * - Manages notifications for user feedback
 *
 * @param {Object} responsiveStyles - Responsive style overrides
 */
export default function IntegrationsList({ responsiveStyles = {} }) {
  const { integrationService } = useContainer();
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const role = useRole();
  const { id, organizationCode } = currentUser || {};
  const { data: orgDetails } = useOrganizationDetails(organizationCode);
  const {
    mutateAsync: updateOrgnaziationMetadata,
    isPending: isUpdatingRosterSync,
  } = useUpdateOrganization(organizationCode);

  // Determine if user is a parent org admin/viewer (multi-org context)
  const isParent =
    role === Role.MULTI_ORG_ADMIN.id || role === Role.MULTI_ORG_VIEWER.id;

  // Only single/child organizations can use this feature
  const canToggleRosterSync =
    canShowRosterSync(orgDetails) && role === Role.ORG_ADMIN.id;

  // Component state
  const [loading, setLoading] = useState(false);
  const [showAddIntegrationModal, setShowAddIntegrationModal] = useState(false);
  const [addIntegrationFailed, setAddIntegrationFailed] = useState(false);
  const [providerDataForNewIntegration, setProviderDataForNewIntegration] =
    useState(null);
  const [preconfigState, setPreconfigState] = useState(false);
  const [lastRunDate, setLastRunDate] = useState(""); // Used to trigger sync status polling
  const [lastRunId, setLastRunId] = useState(null); // Connection ID to monitor for sync completion
  const [showRosterConfirm, setShowRosterConfirm] = useState(false);
  const [pendingRosterValue, setPendingRosterValue] = useState(false);
  // Workaround: ToggleButton flips its own state on click; bumping this key remounts it so it
  // stays on the applied value until the change is confirmed and persisted.
  const [rosterToggleKey, setRosterToggleKey] = useState(0);

  // Custom hooks for managing complex functionality
  const { notification, showError, showSuccess, clearNotification } =
    useNotifications();

  // Handle OAuth window closure (user cancelled or closed window)
  const handleOAuthWindowClosed = () => {
    showError(
      `An error occurred while adding new integration. Please try again.`
    );
    setLoading(false);
  };

  const { authUrl, connectionId, openOAuthWindow, closeOAuthWindow } =
    useOAuthFlow(handleOAuthWindowClosed);

  // Fetch all providers (including disabled ones) to determine static connection context
  const { data: providerList = [] } = useProviders({ includeDisabled: true });

  // Determine if user has a static connection (ClassLink/Clever) based on their IDP
  const { hasStaticConnection, providerDetails } = getStaticIntegrationContext(
    currentUser,
    providerList
  );

  const { data: enabledProvidersList = [] } = useProviders(
    { includeDisabled: false },
    { enabled: !hasStaticConnection }
  );

  const {
    loading: connectionListLoading,
    response: connectionList = [],
    error: connectionListError,
    sendRequest: requestConnectionList,
  } = useApiRequest(
    () => integrationService.getConnectionsList(organizationCode),
    !hasStaticConnection
  );

  const {
    data: staticLastRunMapping,
    isFetching: isFetchingStaticLastRun,
    error: staticLastRunError,
    refetch: requestStaticLastRun,
  } = useConnectionProviderLastRun(
    providerDetails?.providerName,
    [organizationCode],
    { enabled: hasStaticConnection }
  );

  const staticLastRun = staticLastRunMapping?.[organizationCode] ?? null;

  // For parent org users: manage pagination of child org connections
  const {
    rows: parentOrgRows,
    isBuilding: buildingConnections,
    canLoadMore: canLoadMoreChildOrgs,
    loadNextPage: loadNextChildOrgPage,
    refreshRows: refreshParentOrgRows,
  } = useParentOrgPagination(
    organizationCode,
    providerList,
    currentUser,
    hasStaticConnection,
    providerDetails
  );

  // Handle sync status changes from polling (called when sync completes)
  const handleSyncStatusChange = (status) => {
    switch (status) {
      case "Failed": {
        showError(
          `An error occurred during your last update. Please try again.`
        );

        break;
      }
      case "Success": {
        showSuccess(`Your integration has been updated.`);

        break;
      }
      case "error": {
        showError(
          `An error occurred while checking sync status. Please refresh the page.`
        );
        break;
      }
    }
  };

  // Poll for sync status changes after a manual sync is triggered
  // Only active when lastRunDate and lastRunId are set
  useSyncStatusPolling(
    lastRunDate,
    lastRunId,
    organizationCode,
    hasStaticConnection,
    providerDetails,
    handleSyncStatusChange
  );

  useEffect(() => {
    if (connectionListError && !hasStaticConnection) {
      showError(
        `An error occurred while loading integrations. Please try refreshing the page.`
      );
    }

    if (staticLastRunError && hasStaticConnection) {
      showError(
        `An error occurred while loading integration status. Please try refreshing the page.`
      );
    }
  }, [connectionListError, hasStaticConnection, staticLastRunError, showError]);

  // Poll OAuth connection status while OAuth flow is active
  // Checks every 1.5 seconds to detect when user completes OAuth in popup window
  useEffect(() => {
    if (!authUrl || !connectionId) {
      return;
    }

    const checkOauthStatus = async () => {
      try {
        const oauthStatus = await integrationService.getConnectionStatus(
          connectionId
        );
        if (oauthStatus === "active") {
          // OAuth completed successfully
          closeOAuthWindow();

          if (preconfigState) {
            // If preconfig is needed, mark it as active (modal will handle next step)
            setPreconfigState("active");
          } else {
            // Standard OAuth flow - show success and refresh data
            showSuccess(
              `Your ${providerDataForNewIntegration?.name} account has successfully connected with Transfr. Please wait a few minutes while we import your remaining data.`
            );
            requestConnectionList();
            queryClient.invalidateQueries({
              queryKey: ["integrations", organizationCode],
            });
          }

          setLoading(false);
        } else if (oauthStatus === "failed") {
          // OAuth failed
          closeOAuthWindow();
          showError(
            `An error occurred while adding new integration. Please try again.`
          );
          setLoading(false);
        }
        // If status is "pending", continue polling
      } catch (error) {
        console.error("ERROR:", error);
        setLoading(false);
        closeOAuthWindow();
      }
    };

    const intervalId = setInterval(checkOauthStatus, 1500);
    return () => clearInterval(intervalId);
  }, [
    authUrl,
    connectionId,
    preconfigState,
    providerDataForNewIntegration,
    organizationCode,
    requestConnectionList,
    queryClient,
    closeOAuthWindow,
    showSuccess,
    showError,
  ]);

  const onSetShowIntegration = async (value) => {
    try {
      const providerInfo = await integrationService.getProviderData(value.id);
      setProviderDataForNewIntegration(providerInfo);
      setShowAddIntegrationModal(true);
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Handles adding a new integration connection.
   *
   * Flow:
   * 1. Create the connection via API
   * 2. If OAuth is required, open OAuth popup window
   * 3. If not OAuth, refresh the connection list immediately
   * 4. Handle preconfig state if needed (some providers require configuration)
   *
   * @param {Object} data - Integration configuration data
   * @param {string|number} providerId - Provider ID
   * @param {boolean} isPreconfig - Whether this is a preconfig step
   */
  const onAddIntegration = async (data, providerId, isPreconfig = false) => {
    if (isPreconfig) {
      // Preconfig flow: keep modal open but mark as preconfig state
      setPreconfigState(true);
    } else {
      setShowAddIntegrationModal(false);
      setLoading(true);
    }
    try {
      const connectionInfo =
        await integrationService.createIntegrationConnection(
          providerId,
          organizationCode,
          id,
          data
        );

      if (connectionInfo) {
        setAddIntegrationFailed(false);
        if (providerDataForNewIntegration?.authType.includes("oauth")) {
          // OAuth flow: get auth URL and open popup window
          const authUrl = await integrationService.getAuthUrl(
            providerId,
            connectionInfo
          );
          openOAuthWindow(authUrl, connectionInfo);
        } else {
          // Non-OAuth flow: connection is complete, refresh list
          setLoading(false);
          refresh();
        }
      }
    } catch (error) {
      console.error("ERROR:", error);
      setLoading(false);
      setAddIntegrationFailed(true);
      // Reopen modal to show error state
      setShowAddIntegrationModal(false);
      setShowAddIntegrationModal(true);
    }
  };

  const handlePreconfigSync = async (updatedTree) => {
    setShowAddIntegrationModal(false);
    setLoading(true);
    try {
      await integrationService.savePreconfigTree(
        connectionId,
        organizationCode,
        updatedTree
      );
      await integrationService.syncIntegration(connectionId, id);
      showSuccess(
        `Your ${providerDataForNewIntegration?.name} account has successfully connected with Transfr. Please wait a few minutes while we import your remaining data.`
      );
    } catch (error) {
      console.error("ERROR:", error);
    } finally {
      await refresh();
      setLoading(false);
      setProviderDataForNewIntegration(null);
      setPreconfigState(false);
    }
  };

  const handleAddIntegrationCancel = () => {
    setShowAddIntegrationModal(false);
    setProviderDataForNewIntegration(null);
  };

  // Determine which connection list to display based on user type and connection type
  const displayConnectionList = isParent
    ? parentOrgRows // Parent org: show paginated child org connections
    : hasStaticConnection
    ? staticLastRun
      ? [buildStaticConnectionFromLastRun(staticLastRun, providerDetails)] // Static: build from last run data
      : [] // Static but no last run data yet
    : connectionList; // Single org: show direct connections

  const onRosterToggleChange = () => {
    setPendingRosterValue(!orgDetails?.rosterSyncEnabled);
    setShowRosterConfirm(true);
    setRosterToggleKey((k) => k + 1);
  };

  const onConfirmRosterChange = async () => {
    await updateOrgnaziationMetadata(
      { rosterSyncEnabled: pendingRosterValue },
      {
        onSuccess: () => {
          setShowRosterConfirm(false);
          showSuccess(
            pendingRosterValue
              ? "Rostering has been resumed."
              : "Rostering has been paused."
          );
        },
        onError: () => {
          setShowRosterConfirm(false);
          showError("Unable to update rostering. Please try again.");
        },
      }
    );
  };

  const pageHeader = (
    <PageHeader
      title={"Integrations"}
      badge={displayConnectionList?.length || "..."}
      subTitle={"Quickly pull in data from third party tools."}
    >
      {canToggleRosterSync && (
        <div className="roster-sync-toggle">
          <span className="roster-sync-toggle-label">Rostering</span>
          <ToggleButton
            key={rosterToggleKey}
            value={!!orgDetails?.rosterSyncEnabled}
            onChange={onRosterToggleChange}
            disabled={isUpdatingRosterSync}
          />
        </div>
      )}
      {!hasStaticConnection && (
        <TextTooltip
          text="Create Integration"
          className="integration-select-tooltip"
          theme="dropdown"
          position="bottom-end"
        >
          <IntegrationSelect
            providers={enabledProvidersList}
            onSelectProvider={onSetShowIntegration}
            icon={["fa-regular", "plus"]}
          />
        </TextTooltip>
      )}
    </PageHeader>
  );

  const showOverlay =
    loading ||
    buildingConnections ||
    connectionListLoading ||
    (hasStaticConnection && isFetchingStaticLastRun);

  /**
   * Refreshes the connection list data.
   *
   * If refreshLastRunDate and refreshLastRunId are provided, this indicates a
   * manual sync was triggered, so we start polling for sync completion.
   *
   * @param {string} refreshLastRunDate - Last run date (triggers sync polling)
   * @param {string|number} refreshLastRunId - Connection ID to monitor
   */
  const refresh = async (refreshLastRunDate, refreshLastRunId) => {
    // If sync was triggered, start polling for completion
    if (refreshLastRunDate && refreshLastRunId) {
      setLastRunDate(refreshLastRunDate);
      setLastRunId(refreshLastRunId);
      setLoading(true);
    }
    if (isParent) {
      // Parent org: refresh paginated child org connections
      try {
        await refreshParentOrgRows();
      } catch (error) {
        console.error(
          "[Integrations] Error refreshing parent connections:",
          error
        );
        showError(
          `An error occurred while refreshing integrations. Some data may not be displayed.`
        );
      } finally {
        setLoading(false);
      }
    } else if (hasStaticConnection) {
      // Static connection: refresh last run data
      try {
        await requestStaticLastRun();
      } catch (error) {
        console.error(
          "[Integrations] Error refreshing static last run:",
          error
        );
        showError(`An error occurred while refreshing integration status.`);
      }
    } else {
      // Dynamic connection: refresh connection list
      try {
        await requestConnectionList();
        queryClient.invalidateQueries({
          queryKey: ["integrations", organizationCode],
        });
      } catch (error) {
        console.error(
          "[Integrations] Error refreshing connection list:",
          error
        );
        showError(`An error occurred while refreshing integrations.`);
      }
    }
  };

  return (
    <PageLayout
      className="integrations-list-page"
      responsiveStyles={responsiveStyles}
      header={pageHeader}
    >
      <div className="integrations-table-container">
        {showOverlay && <Loader overlay />}
        {!!displayConnectionList && displayConnectionList.length > 0 ? (
          <IntegrationTable
            connectionList={displayConnectionList}
            launchNotification={({ type, message, icon }) => {
              if (type === "error") {
                showError(message, icon);
              } else {
                showSuccess(message, icon);
              }
            }}
            refresh={refresh}
            isLoading={loading}
            isStaticConnection={hasStaticConnection}
          />
        ) : (
          <ZeroStateMessage
            providers={hasStaticConnection ? [] : enabledProvidersList}
            onActionClicked={onSetShowIntegration}
          />
        )}
        {isParent && canLoadMoreChildOrgs && !showOverlay && (
          <div className="load-more-container">
            <Button
              onClick={loadNextChildOrgPage}
              disabled={buildingConnections}
            >
              Load More
            </Button>
          </div>
        )}
      </div>
      <div className="notification-container success-notification">
        {notification && (
          <Notification
            type={notification.type}
            icon={notification.icon}
            onClose={clearNotification}
            closable
          >
            {notification.message}
          </Notification>
        )}
      </div>
      {showAddIntegrationModal && (
        <AddIntegrationModal
          onCancel={handleAddIntegrationCancel}
          open={showAddIntegrationModal}
          onAddIntegration={onAddIntegration}
          provider={providerDataForNewIntegration}
          addIntegrationFailed={addIntegrationFailed}
          connectionId={connectionId}
          preconfigState={preconfigState}
          clientCode={organizationCode}
          onPreconfigSync={handlePreconfigSync}
        />
      )}
      {canToggleRosterSync && (
        <RosterSyncConfirmModal
          open={showRosterConfirm}
          enabling={pendingRosterValue}
          onCancel={() => setShowRosterConfirm(false)}
          onConfirm={onConfirmRosterChange}
        />
      )}
    </PageLayout>
  );
}
